import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const SECTIONS = [
  {
    n: "1",
    title: "About Alteza",
    body: [
      "Alteza Marketplace is an online marketplace dedicated to supporting handmade and small business brands. Our mission is to help makers reach new customers while building a community-focused shopping experience.",
    ],
  },
  {
    n: "2",
    title: "Seller Eligibility",
    intro: "To sell on Alteza, sellers must:",
    list: [
      "Be at least 18 years of age.",
      "Own or have the legal right to sell the products listed.",
      "Provide accurate business and payment information.",
      "Comply with all applicable laws and regulations.",
    ],
  },
  {
    n: "3",
    title: "Commission and Fees",
    list: [
      "Alteza charges an 8% commission on completed sales.",
      "There are currently no monthly seller fees.",
      "Payment processing fees charged by third-party payment providers (such as Stripe) are the responsibility of the seller.",
      "Alteza reserves the right to update commission rates in the future. Existing founding sellers will be notified of any changes.",
    ],
  },
  {
    n: "4",
    title: "Fees & Commission",
    badge: "Updated",
    body: [
      "Sellers may create an account and list products at no cost.",
      "Alteza Marketplace charges an 8% commission on completed sales.",
      "In addition to Alteza's commission, Stripe payment processing fees will apply to all transactions. These fees are charged by Stripe and may vary based on the transaction amount, payment method, and country of origin.",
      "By selling on Alteza Marketplace, sellers acknowledge and agree that both the Alteza Marketplace commission and applicable Stripe processing fees will be deducted from eligible transactions in accordance with the platform's payment structure.",
      "Sellers are responsible for reviewing and understanding Stripe's current fee schedule.",
    ],
  },
  {
    n: "5",
    title: "Founding Seller Benefits",
    intro: "Founding sellers may receive:",
    list: [
      "Featured placement on Alteza Marketplace.",
      "Promotion through Alteza social media channels.",
      "Opportunities to be highlighted in marketing campaigns.",
      "Early access to new marketplace features.",
    ],
    outro: "These benefits are provided at Alteza's discretion and may change over time.",
  },
  {
    n: "6",
    title: "Product Listings",
    intro: "Sellers are responsible for:",
    list: [
      "Accurate product descriptions.",
      "Product pricing.",
      "Inventory management.",
      "Product photography.",
      "Compliance with applicable safety regulations.",
    ],
    outro: "Products that are counterfeit, illegal, unsafe, or misleading are prohibited.",
  },
  {
    n: "7",
    title: "Orders and Fulfillment",
    intro: "Sellers are responsible for:",
    list: [
      "Processing orders promptly.",
      "Packaging products appropriately.",
      "Shipping products within the timeframe stated in their listings.",
      "Providing accurate tracking information when available.",
    ],
  },
  {
    n: "8",
    title: "Returns and Refunds",
    body: [
      "Sellers are responsible for maintaining and honoring their return and refund policies.",
      "If a customer dispute arises, Alteza may assist in resolving the issue but is not responsible for refunds unless required by law.",
    ],
  },
  {
    n: "9",
    title: "Intellectual Property",
    body: [
      "Sellers retain ownership of their products, photos, branding, and content.",
      "By listing products on Alteza, sellers grant Alteza permission to use product images, descriptions, and branding for promotional and marketing purposes.",
    ],
  },
  {
    n: "10",
    title: "Account Suspension or Removal",
    intro: "Alteza reserves the right to suspend or remove seller accounts that:",
    list: [
      "Violate these terms.",
      "Receive repeated customer complaints.",
      "Sell prohibited products.",
      "Engage in fraudulent or deceptive practices.",
    ],
  },
  {
    n: "11",
    title: "Limitation of Liability",
    body: [
      "Alteza provides the marketplace platform but is not responsible for product quality, shipping delays, seller actions, or customer misuse of products.",
    ],
  },
  {
    n: "12",
    title: "Changes to This Agreement",
    body: [
      "Alteza may update this Seller Agreement from time to time. Continued use of the platform constitutes acceptance of any updated terms.",
    ],
  },
  {
    n: "13",
    title: "Contact",
    body: [
      "Questions regarding this agreement may be directed to Alteza Marketplace through our official contact channels.",
    ],
  },
];

export default function SellerAgreement() {
  return (
    <div data-testid="seller-agreement-page" className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <Link to="/sell" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-10">
        <ArrowLeft className="w-4 h-4" /> Back to seller info
      </Link>

      <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-4">◦ Legal</div>
      <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Alteza Marketplace Seller Agreement</h1>
      <p className="text-sm text-muted-foreground mt-4">Last Updated: June 2026</p>

      <p className="mt-8 text-base leading-relaxed text-foreground/90">
        Welcome to Alteza Marketplace. By joining and selling on Alteza, you agree to the following terms.
      </p>

      <div className="mt-12 space-y-12">
        {SECTIONS.map((s) => (
          <section key={s.n} data-testid={`agreement-section-${s.n}`}>
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="font-serif text-2xl tracking-tight">
                <span className="text-primary mr-2">{s.n}.</span>{s.title}
              </h2>
              {s.badge && (
                <span className="founding-badge text-[9px] tracking-[0.1em] uppercase font-semibold px-1.5 py-0.5 rounded-sm">{s.badge}</span>
              )}
            </div>
            <div className="mt-4 space-y-3 text-base leading-relaxed text-foreground/90">
              {s.body?.map((p, i) => <p key={i}>{p}</p>)}
              {s.intro && <p>{s.intro}</p>}
              {s.list && (
                <ul className="list-disc pl-6 space-y-2 marker:text-primary">
                  {s.list.map((li, i) => <li key={i}>{li}</li>)}
                </ul>
              )}
              {s.outro && <p>{s.outro}</p>}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-14 pt-8 border-t border-border">
        <p className="text-base leading-relaxed font-medium">
          By joining Alteza Marketplace, you acknowledge that you have read and agree to these terms.
        </p>
        <Link
          to="/register?role=seller"
          data-testid="agreement-apply-btn"
          className="btn-primary inline-flex items-center gap-2 mt-8 px-7 py-3.5 text-sm rounded-sm"
        >
          Open my shop
        </Link>
      </div>
    </div>
  );
}
